import format, { FormatDurationOptions } from "format-duration";

export const millisToDate = (millis: number): string =>
  new Date(millis).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const millisToTime = (millis: number, showSeconds = false): string =>
  new Date(millis).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    ...(showSeconds ? { second: "2-digit" } : {}),
  });

export function millisToDateTime(
  millis: number,
  version: "long" | "short",
): string;
export function millisToDateTime(millis: number): string;
export function millisToDateTime(
  millis: number,
  version?: "long" | "short",
): string {
  switch (version) {
    case "short":
      return new Date(millis).toLocaleTimeString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    case "long":
    default:
      return `${new Date(millis).toLocaleTimeString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })} Uhr`;
  }
}

export const millisToTimeRemaining = (
  millis: number,
  options?: FormatDurationOptions,
) => {
  return format(millis, options);
};

export const timeRemainingToMillis = (durationString: string): number => {
  if (!durationString || durationString.trim() === "") {
    throw new Error("Eingabe darf nicht leer sein.");
  }

  const trimmedString = durationString.trim();

  // Regex to find all leading zeros and colons
  const leadingZeroRegex = /^([0:]+)/;
  let cleanedString = trimmedString.replace(leadingZeroRegex, "");

  // If replacement resulted in an empty string, it means the original
  // string was all zeros/colons (e.g., "0", "0:00").
  // In this case, we treat it as "0".
  if (cleanedString === "" && trimmedString.length > 0) {
    cleanedString = "0";
  }

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (cleanedString.includes(":")) {
    // --- Colon-separated format (H:M:S) ---
    const parts = cleanedString.split(":");

    if (parts.length > 3) {
      throw new Error("Ungültiges Format.");
    }

    // Parse from right to left (SS, MM, HH)
    const parsedParts = parts.reverse().map((part) => {
      if (part === "" || !/^\d+$/.test(part)) {
        throw new Error("Ungültiges Format.");
      }
      const value = parseInt(part, 10);
      if (isNaN(value)) {
        throw new Error("Ungültiges Format.");
      }
      return value;
    });

    seconds = parsedParts[0];
    minutes = parsedParts[1] || 0;
    hours = parsedParts[2] || 0;
  } else {
    // --- Number-only format (HHMMSS) ---
    if (!/^\d+$/.test(cleanedString)) {
      throw new Error("Ungültiges Format.");
    }

    const len = cleanedString.length;
    const sStr = cleanedString.substring(Math.max(0, len - 2));
    const mStr = cleanedString.substring(Math.max(0, len - 4), len - 2);
    const hStr = cleanedString.substring(0, Math.max(0, len - 4));

    seconds = parseInt(sStr) || 0;
    minutes = parseInt(mStr) || 0;
    hours = parseInt(hStr) || 0;
  }

  // Validate ranges
  if (seconds > 59) {
    throw new Error("Ungültiges Format."); // Invalid seconds
  }
  if (minutes > 59) {
    throw new Error("Ungültiges Format."); // Invalid minutes
  }

  return (hours * 3600 + minutes * 60 + seconds) * 1000;
};

export const millisToDateAtMidnight = (millis: number): number => {
  const date = new Date(millis);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};
